import { useCallback, useEffect, useState } from "react";
import { RuntimeSetting, api, isError } from "../api";

export function SettingsPanel() {
  const [settings, setSettings] = useState<RuntimeSetting[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.listSettings();
    if (isError(r)) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setSettings(r.settings);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggle(s: RuntimeSetting) {
    setBusyKey(s.key);
    setMessage(null);
    const r = await api.setSetting(s.key, !s.value);
    setBusyKey(null);
    if (isError(r)) {
      setMessage(`Update failed: ${r.error}`);
      return;
    }
    setSettings(r.settings);
    setMessage(`Updated ${s.label}.`);
  }

  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">Settings</h2>
        <span className="text-xs text-fg-muted">
          runtime feature toggles — survive restarts
        </span>
      </header>

      {loadError && (
        <p className="mb-3 rounded border border-danger/40 bg-danger/10 px-2 py-1 text-sm text-danger">
          Couldn’t load settings: {loadError}
        </p>
      )}
      {message && (
        <p className="mb-3 text-xs text-fg-muted">{message}</p>
      )}

      {settings === null && !loadError ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <ul className="divide-y divide-border">
          {settings?.map((s) => (
            <li
              key={s.key}
              className="flex items-start justify-between gap-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg">
                  {s.label}
                  {s.is_override && (
                    <span
                      className="ml-2 rounded bg-warn/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-warn"
                      title={`Overrides env default of ${s.default ? "on" : "off"}`}
                    >
                      override
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
                  {s.description}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void toggle(s)}
                disabled={busyKey === s.key}
                aria-pressed={s.value}
                title={s.value ? "Click to disable" : "Click to enable"}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border transition-colors disabled:cursor-wait disabled:opacity-50 ${
                  s.value
                    ? "border-success bg-success"
                    : "border-border bg-bg-hover"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    s.value ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
